import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchEntities } from 'common/hooks/usePalmFetchEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchUserRoleMemberships } from 'common/services/api/UserRoleMemberships/UserRoleMemberships.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, DrawerEntitiesNames, Operations } from 'common/types/TypeDefs';

import { UserRoleMembershipCreateContent } from './UserRoleMembershipCreateDrawerContent';
import { UserRoleMembershipsDeleteDialog } from './UserRoleMembershipsDeleteDialog';
import { addNewFilter, clearFilters, removeFilter, selectFilters } from './userRoleMembershipsSlice';

export function UserRoleMembershipsTab() {
    const typeName = 'Role';
    const [processing, setProcessing] = useState<boolean>(false);

    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager, Roles.MultiTenantRO, Roles.UserManager ]);
    const shouldDisableDeleteEditButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters);

    const [sortModel, setSortModel] = usePalmSortModel('roleName');
    const { suiteId = '', tenantId ='', userId = '' } = useParams();

    const fetchEntities = usePalmFetchEntities(
        typeName,
        { tenantId, userId },
        fetchUserRoleMemberships,
        setProcessing
    );

    const onGetRowLink = useCallback((roleId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Roles}/${roleId}/${Routes.Users}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
        { action: Operations.Delete, disabled: shouldDisableDeleteEditButton },
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'roleId', columnName: 'Role ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'roleName', columnName: 'Role Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        title='Role'
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        operationName={Operations.Link}
        filter={filterProp}
        contentFunction={UserRoleMembershipCreateContent}
        isCreateButtonDisabled={shouldDisableDeleteEditButton}
        refreshDataGridClick={() => fetchEntities()}
        getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
        deleteFunction={UserRoleMembershipsDeleteDialog}
        customDeleteTitle={'Are you sure you want to remove this User from this Role?'}
        currentEntityName={DrawerEntitiesNames.AppSuiteUserRoleMembership}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
